import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PrStatusIcon, PrLegend, PrMatrixTable, PR_CYCLE, prThemeVars } from '../../components/progress/PrStatus';

const API = process.env.REACT_APP_API || '/api';
const nowMonth = () => new Date().toISOString().slice(0, 7);

// يبادل عنصراً مع جاره ويعيد قائمة ids بالترتيب الجديد، أو null عند الأطراف
const moveId = (list, id, dir) => {
  const ids = list.map((x) => x.id);
  const i = ids.indexOf(id);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= ids.length) return null;
  [ids[i], ids[j]] = [ids[j], ids[i]];
  return ids;
};

export default function ProgressReportBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, apiUpload, token } = useAuth();
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);

  const [report, setReport] = useState(null);
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState('');
  const [month, setMonth] = useState(nowMonth());
  const [addingProject, setAddingProject] = useState(false);
  const [projForm, setProjForm] = useState({ title: '', sheikh_name: '', source: 'manual', platform_project_id: '', sheikh_id: '', period_mode: 'cumulative' });
  const [platformProjects, setPlatformProjects] = useState([]);
  const [sheikhs, setSheikhs] = useState([]);
  const [preview, setPreview] = useState(null);

  // toast خارج الاعتماديات — هويته تتغير مع كل toast فيعيد إطلاق الجلب بلا نهاية عند الفشل
  const load = useCallback(() => {
    api(`/progress/reports/${id}`).then((r) => {
      setReport(r);
      setMeta((m) => m || {
        title: r.title, donor_name: r.donor_name,
        logo_path: r.logo_path || '', logo2_path: r.logo2_path || '',
        primary_color: r.primary_color || '', accent_color: r.accent_color || '',
        summary: r.summary || '', alerts: r.alerts || '',
        public_slug: r.public_slug || '',
      });
    }).catch((e) => toast.error(e.message));
  }, [api, id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('/projects').then(setPlatformProjects).catch(() => {});
    api('/clients').then((cs) => setSheikhs((Array.isArray(cs) ? cs : []).filter((c) => c.type === 'sheikh'))).catch(() => {});
  }, [api]);

  // مفتاح ثابت كي لا تُعاد معاينة المنصة مع كل نقرة خلية (تحديث تفاؤلي يغيّر report كاملاً)
  const platformKey = useMemo(
    () => (report?.projects || [])
      .filter((p) => p.source === 'platform')
      .map((p) => `${p.id}:${p.platform_project_id}`)
      .join(','),
    [report],
  );
  // المعاينة تتبع شهر النشر المحدد — الأقسام بنطاق الشهر تُفلتر أعمالها به.
  // حارس تقادم: استجابة شهر سابق وصلت متأخرة يجب ألا تدهس شهر المعاينة الحالي
  useEffect(() => {
    if (!platformKey) return undefined;
    let stale = false;
    setPreview(null);
    api(`/progress/reports/${id}/preview${month ? `?month=${month}` : ''}`)
      .then((r) => { if (!stale) setPreview(r); })
      .catch(() => { if (!stale) setPreview('error'); });
    return () => { stale = true; };
  }, [api, id, platformKey, month]);

  const save = async (patch, msg) => {
    try {
      await api(`/progress/reports/${id}`, { method: 'PUT', body: JSON.stringify(patch) });
      if (msg) toast.success(msg);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const uploadLogo = async (field, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiUpload('/upload', fd);
      setMeta((m) => ({ ...m, [field]: res.url }));
      await save({ [field]: res.url }, 'تم رفع الشعار');
    } catch (e) { toast.error(e.message); }
  };

  const addProject = async () => {
    try {
      const body = { title: projForm.title, sheikh_name: projForm.sheikh_name || null, source: projForm.source };
      if (projForm.source === 'platform') {
        body.platform_project_id = Number(projForm.platform_project_id);
        body.sheikh_id = projForm.sheikh_id ? Number(projForm.sheikh_id) : null;
        body.period_mode = projForm.period_mode;
      }
      await api(`/progress/reports/${id}/projects`, { method: 'POST', body: JSON.stringify(body) });
      toast.success('تمت إضافة المشروع');
      setProjForm({ title: '', sheikh_name: '', source: 'manual', platform_project_id: '', sheikh_id: '', period_mode: 'cumulative' });
      setAddingProject(false);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const publish = async () => {
    try {
      await api(`/progress/reports/${id}/publish`, { method: 'POST', body: JSON.stringify({ month }) });
      toast.success(`تم نشر تقرير ${month}`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  const downloadPdf = async (monthParam) => {
    setDownloading(true);
    try {
      const qs = monthParam ? `?month=${monthParam}` : '';
      const res = await fetch(`${API}/progress/reports/${id}/pdf${qs}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' },
      });
      if (!res.ok) throw new Error('تعذّر توليد الملف');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = monthParam && monthParam !== 'preview' ? `-${monthParam}` : '';
      a.download = `تقرير-${report.donor_name}${suffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || 'تعذّر التوليد'); } finally { setDownloading(false); }
  };

  const moveProject = async (projectId, dir) => {
    const ids = moveId(report.projects || [], projectId, dir);
    if (!ids) return;
    try {
      await api(`/progress/reports/${id}/reorder`, { method: 'PUT', body: JSON.stringify({ project_ids: ids }) });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const destroyReport = async () => {
    if (!window.confirm('حذف التقرير بكل مشاريعه وأشهره المنشورة؟')) return;
    try {
      await api(`/progress/reports/${id}`, { method: 'DELETE' });
      toast.success('تم حذف التقرير');
      navigate('/progress-reports');
    } catch (e) { toast.error(e.message); }
  };

  if (!report || !meta) return <div className="loader">جاري التحميل...</div>;

  const portalUrl = report.public_slug ? `${window.location.origin}/progress/${report.public_slug}` : null;

  return (
    <div style={prThemeVars(meta)}>
      {/* بيانات التقرير */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title">🗂 {report.donor_name}</div>
          <button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)' }} onClick={destroyReport}>حذف التقرير</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 12 }}>
          <Field label="اسم الجهة المانحة *">
            <input className="form-input" value={meta.donor_name} onChange={(e) => setMeta({ ...meta, donor_name: e.target.value })} />
          </Field>
          <Field label="عنوان التقرير">
            <input className="form-input" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
          </Field>
          <Field label="شعار (أساسي)">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {meta.logo_path && <img src={meta.logo_path} alt="" style={{ maxHeight: 34 }} />}
              <input type="file" accept="image/*" onChange={(e) => uploadLogo('logo_path', e.target.files[0])} style={{ fontSize: 12 }} />
            </div>
          </Field>
          <Field label="شعار (ثانٍ)">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {meta.logo2_path && <img src={meta.logo2_path} alt="" style={{ maxHeight: 34 }} />}
              <input type="file" accept="image/*" onChange={(e) => uploadLogo('logo2_path', e.target.files[0])} style={{ fontSize: 12 }} />
            </div>
          </Field>
          <Field label="اللون الأساسي (الغلاف وترويسات الجداول)">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="color" value={meta.primary_color || '#1f5c3f'} onChange={(e) => setMeta({ ...meta, primary_color: e.target.value })}
                style={{ width: 44, height: 30, padding: 0, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }} />
              {meta.primary_color && <button className="btn btn-sm btn-outline" onClick={() => setMeta({ ...meta, primary_color: '' })}>الافتراضي</button>}
            </div>
          </Field>
          <Field label="اللون الثانوي (عناوين الجداول والفواصل)">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="color" value={meta.accent_color || '#b08d34'} onChange={(e) => setMeta({ ...meta, accent_color: e.target.value })}
                style={{ width: 44, height: 30, padding: 0, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }} />
              {meta.accent_color && <button className="btn btn-sm btn-outline" onClick={() => setMeta({ ...meta, accent_color: '' })}>الافتراضي</button>}
            </div>
          </Field>
        </div>
        <div style={{ marginTop: 10 }}>
          <button className="btn btn-primary btn-sm"
            onClick={() => save({ title: meta.title, donor_name: meta.donor_name, primary_color: meta.primary_color || null, accent_color: meta.accent_color || null }, 'تم الحفظ')}>حفظ البيانات</button>
        </div>
      </div>

      {/* النشر والمشاركة */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 10 }}>🔗 الرابط العام والنشر</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          <Field label="معرّف الرابط (أحرف إنجليزية وأرقام وشرطات)">
            <input className="form-input" dir="ltr" placeholder="saud-foundation" value={meta.public_slug}
              onChange={(e) => setMeta({ ...meta, public_slug: e.target.value })}
              onBlur={() => meta.public_slug !== (report.public_slug || '') && save({ public_slug: meta.public_slug || null })} />
          </Field>
          <Field label="كلمة مرور (اختياري)">
            <div style={{ display: 'flex', gap: 6 }}>
              <input className="form-input" type="password" placeholder={report.is_protected ? '•••••• (مضبوطة)' : 'بلا حماية'} value={password} onChange={(e) => setPassword(e.target.value)} />
              <button className="btn btn-outline btn-sm" disabled={!password} onClick={() => { save({ public_password: password }, 'تم ضبط كلمة المرور'); setPassword(''); }}>ضبط</button>
              {report.is_protected && <button className="btn btn-outline btn-sm" onClick={() => save({ clear_password: true }, 'أُزيلت كلمة المرور')}>إزالة</button>}
            </div>
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={report.public_enabled} onChange={(e) => save({ public_enabled: e.target.checked }, e.target.checked ? 'فُعّل الرابط العام' : 'عُطّل الرابط العام')} />
            تفعيل الرابط العام
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={report.live_enabled} onChange={(e) => save({ live_enabled: e.target.checked }, e.target.checked ? 'فُعّلت المتابعة اللحظية' : 'عُطّلت المتابعة اللحظية')} />
            المتابعة اللحظية (يظهر للعميل تبويب يعرض الحالة الآن)
          </label>
        </div>
        {portalUrl && report.public_enabled && (
          <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
            <a href={portalUrl} target="_blank" rel="noreferrer" dir="ltr">{portalUrl}</a>
            <button className="btn btn-outline btn-sm" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success('نُسخ الرابط'); }}>📋 نسخ</button>
          </div>
        )}
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="form-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: 170 }} />
          <button className="btn btn-primary btn-sm" onClick={publish}>📤 نشر لقطة هذا الشهر</button>
          <button className="btn btn-outline btn-sm" onClick={() => downloadPdf('preview')} disabled={downloading}>
            {downloading ? '⏳ جارٍ التوليد...' : '⬇ تحميل PDF (المعاينة الحالية)'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>النشر يجمّد الحالة الحالية كتقرير للشهر المحدد — إعادة النشر تنشئ «نسخة منقحة» وتحفظ السابقة.</span>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, background: 'var(--c-gold-soft)', color: 'var(--c-gold-ink)', borderRadius: 6, padding: '6px 10px' }}>
          ⚠️ الرابط العام يعرض <strong>آخر شهر منشور</strong> — تعديلاتك هنا لا تظهر للعميل حتى تعيد نشر الشهر من الزر أعلاه (أو عبر تبويب «المتابعة اللحظية» إن فعّلتها).
        </div>
        {report.periods?.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {/* النسخ المنقحة: تظهر أحدث نسخة لكل شهر، وحذفها يُظهر النسخة السابقة إن وُجدت */}
            {report.periods.filter((p, i, arr) => arr.findIndex((x) => x.report_month?.slice(0, 7) === p.report_month?.slice(0, 7)) === i).map((p) => (
              <span key={p.id} className="badge" style={{ background: 'var(--surface-alt)', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                {p.report_month?.slice(0, 7)}
                {p.version > 1 && <span title={`نسخة منقحة — الإصدار ${p.version}`} style={{ fontSize: 10, color: 'var(--text-muted)' }}>ن{p.version}</span>}
                {p.status === 'draft' && (
                  <button className="btn btn-link btn-sm" style={{ padding: 0 }} title="المسودّة لا تظهر في البوابة ولا تُرسل"
                    onClick={async () => { try { await api(`/progress/periods/${p.id}/publish`, { method: 'POST' }); toast.success('نُشرت المسودّة'); load(); } catch (e) { toast.error(e.message); } }}>
                    مسودّة — نشر
                  </button>
                )}
                {portalUrl && <a href={`${portalUrl}?month=${p.report_month?.slice(0, 7)}`} target="_blank" rel="noreferrer">عرض</a>}
                <span style={{ cursor: 'pointer', color: 'var(--report-brand)' }} title="تحميل PDF لهذا الشهر"
                  onClick={() => downloadPdf(p.report_month?.slice(0, 7))}>⬇</span>
                <span style={{ cursor: 'pointer', color: 'var(--danger)' }} title="حذف هذه النسخة المنشورة"
                  onClick={async () => { if (window.confirm(p.version > 1 ? 'حذف هذه النسخة المنقحة؟ ستظهر النسخة السابقة لنفس الشهر مكانها.' : 'حذف هذا الشهر المنشور؟')) { try { await api(`/progress/periods/${p.id}`, { method: 'DELETE' }); load(); } catch (e) { toast.error(e.message); } } }}>×</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <ReportDeliveryPanel report={report} month={month} sheikhs={sheikhs} onChange={load} />

      {/* المشاريع */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="card-title">📂 مشاريع التقرير</div>
          <button className="btn btn-primary btn-sm" onClick={() => setAddingProject((v) => !v)}>＋ إضافة مشروع</button>
        </div>

        {addingProject && (
          <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: 12, marginTop: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <Field label="اسم المشروع *">
                <input className="form-input" value={projForm.title} onChange={(e) => setProjForm({ ...projForm, title: e.target.value })} placeholder="أعمال مشروع تلخيص كتب العقيدة" />
              </Field>
              <Field label="الشيخ / الجهة (يظهر في التقرير)">
                <input className="form-input" value={projForm.sheikh_name} onChange={(e) => setProjForm({ ...projForm, sheikh_name: e.target.value })} placeholder="لمعالي الشيخ ..." />
              </Field>
              <Field label="مصدر البيانات">
                <select className="form-input" value={projForm.source} onChange={(e) => setProjForm({ ...projForm, source: e.target.value })}>
                  <option value="manual">يدوي — أعبّئ الجداول بنفسي كل شهر</option>
                  <option value="platform">من المنصة — يُشتق من مهام مشروع</option>
                </select>
              </Field>
              {projForm.source === 'platform' && (
                <Field label="مشروع المنصة *">
                  <select className="form-input" value={projForm.platform_project_id} onChange={(e) => setProjForm({ ...projForm, platform_project_id: e.target.value })}>
                    <option value="">— اختر —</option>
                    {platformProjects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </Field>
              )}
              {projForm.source === 'platform' && (
                <Field label="أعمال شيخ محدد فقط (يفصل أعمال كل شيخ في قسمه)">
                  <select className="form-input" value={projForm.sheikh_id} onChange={(e) => setProjForm({ ...projForm, sheikh_id: e.target.value })}>
                    <option value="">كل المهام</option>
                    {sheikhs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </Field>
              )}
              {projForm.source === 'platform' && (
                <Field label="نطاق الأعمال">
                  <select className="form-input" value={projForm.period_mode} onChange={(e) => setProjForm({ ...projForm, period_mode: e.target.value })}>
                    <option value="cumulative">مستمر — كل أعمال المشروع</option>
                    <option value="monthly">الأعمال النشطة خلال شهر التقرير</option>
                  </select>
                </Field>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn btn-primary btn-sm" onClick={addProject} disabled={!projForm.title || (projForm.source === 'platform' && !projForm.platform_project_id)}>إضافة</button>
              <button className="btn btn-outline btn-sm" onClick={() => setAddingProject(false)}>إلغاء</button>
            </div>
          </div>
        )}

        <GroupsManager reportId={id} groups={report.groups || []} api={api} toast={toast} reload={load} />

        {(report.projects || []).length === 0 && <div className="empty" style={{ marginTop: 10 }}>لا مشاريع بعد.</div>}
        {(report.projects || []).map((project, idx) => (
          <ProjectEditor key={project.id} project={project} api={api} toast={toast} reload={load}
            preview={preview} setReport={setReport} platformProjects={platformProjects} sheikhs={sheikhs} groups={report.groups || []}
            onMove={moveProject} isFirst={idx === 0} isLast={idx === (report.projects.length - 1)} />
        ))}
        <div style={{ marginTop: 12 }}><PrLegend /></div>
      </div>

      {/* الخلاصة والتنبيهات */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title" style={{ marginBottom: 10 }}>📝 خلاصة التقرير والتنبيهات</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
          <Field label="خلاصة التقرير (كل سطر بند)">
            <textarea className="form-input" rows={5} value={meta.summary} onChange={(e) => setMeta({ ...meta, summary: e.target.value })} />
          </Field>
          <Field label="التنبيهات (كل سطر تنبيه)">
            <textarea className="form-input" rows={5} value={meta.alerts} onChange={(e) => setMeta({ ...meta, alerts: e.target.value })} />
          </Field>
        </div>
        <div style={{ marginTop: 10 }}>
          <button className="btn btn-primary btn-sm" onClick={() => save({ summary: meta.summary || null, alerts: meta.alerts || null }, 'تم الحفظ')}>حفظ</button>
        </div>
      </div>
    </div>
  );
}

const projectForm = (p) => ({
  title: p.title, sheikh_name: p.sheikh_name || '', notes: p.notes || '',
  platform_project_id: p.platform_project_id || '', sheikh_id: p.sheikh_id || '',
  group_id: p.group_id || '', period_mode: p.period_mode || 'cumulative',
  color: p.color || '',
});

function ProjectEditor({ project, api, toast, reload, preview, setReport, platformProjects, sheikhs, groups, onMove, isFirst, isLast }) {
  const [editing, setEditing] = useState(false);
  const [configuring, setConfiguring] = useState(false);
  const [form, setForm] = useState(() => projectForm(project));
  const [newMatrix, setNewMatrix] = useState('');

  // إعادة التهيئة عند فتح التحرير — نموذج قديم (بعد توزيع أو حذف مجموعة) كان
  // يعيد group_id بائتاً فيفكّ التجميع بصمت أو يرفضه الخادم
  const toggleEditing = () => setEditing((v) => {
    if (!v) setForm(projectForm(project));
    return !v;
  });

  const saveProject = async () => {
    try {
      const body = {
        title: form.title, sheikh_name: form.sheikh_name || null, notes: form.notes || null,
        group_id: form.group_id ? Number(form.group_id) : null, color: form.color || null,
      };
      if (project.source === 'platform' && form.platform_project_id) {
        body.platform_project_id = Number(form.platform_project_id);
        body.sheikh_id = form.sheikh_id ? Number(form.sheikh_id) : null;
        body.period_mode = form.period_mode;
      }
      await api(`/progress/projects/${project.id}`, { method: 'PUT', body: JSON.stringify(body) });
      toast.success('تم حفظ المشروع');
      setEditing(false);
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const destroy = async () => {
    if (!window.confirm(`حذف مشروع «${project.title}» من التقرير؟`)) return;
    try { await api(`/progress/projects/${project.id}`, { method: 'DELETE' }); reload(); } catch (e) { toast.error(e.message); }
  };

  const addMatrix = async () => {
    try {
      await api(`/progress/projects/${project.id}/matrices`, { method: 'POST', body: JSON.stringify({ title: newMatrix || null }) });
      setNewMatrix('');
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const moveMatrix = async (matrixId, dir) => {
    const ids = moveId(project.matrices || [], matrixId, dir);
    if (!ids) return;
    try {
      await api(`/progress/projects/${project.id}/reorder`, { method: 'PUT', body: JSON.stringify({ matrix_ids: ids }) });
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const splitBySheikh = async () => {
    if (!window.confirm('إنشاء قسم مستقل لكل شيخ له مهام، وتجميع الأقسام تحت مشروع موحّد باسم هذا المشروع؟ (يبقى هذا المشروع المختلط ويمكنك حذفه بعدها)')) return;
    try {
      const res = await api(`/progress/projects/${project.id}/split-by-sheikh`, { method: 'POST' });
      toast.success(`أُنشئ ${res.created} مشروعاً${res.skipped ? ` (تخطّي ${res.skipped} موجود)` : ''} — احذف هذا المشروع المختلط إن لم تعد تحتاجه`);
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const previewProject = preview?.projects?.find((p) => p.id === project.id);
  const group = groups.find((g) => g.id === project.group_id);

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, marginTop: 14, overflow: 'hidden' }}>
      <div style={{ background: project.color || 'linear-gradient(150deg, var(--report-brand) 0%, var(--report-brand-dark) 100%)', color: '#fff', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <strong>{project.title}</strong>
          {project.sheikh_name && <span style={{ opacity: 0.85, marginInlineStart: 8, fontSize: 13 }}>{project.sheikh_name}</span>}
          <span className="badge" style={{ marginInlineStart: 8, background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 11 }}>
            {project.source === 'platform' ? `⚡ من المنصة${project.platform_project ? ` — ${project.platform_project.title}` : ''}` : '✏️ يدوي'}
          </span>
          {project.source === 'platform' && project.sheikh_id && (
            <span className="badge" style={{ marginInlineStart: 6, background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 11 }}>
              🔎 {sheikhs.find((s) => s.id === project.sheikh_id)?.name || 'شيخ محدد'}
            </span>
          )}
          {group && (
            <span className="badge" style={{ marginInlineStart: 6, background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 11 }} title="ضمن مشروع موحّد">
              🗂 {group.title}
            </span>
          )}
          {project.source === 'platform' && project.period_mode === 'monthly' && (
            <span className="badge" style={{ marginInlineStart: 6, background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 11 }} title="يعرض الأعمال النشطة خلال شهر التقرير فقط">
              🗓 نشط خلال الشهر
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" style={miniBtn} disabled={isFirst} title="تقديم المشروع" onClick={() => onMove(project.id, -1)}>▲</button>
          <button className="btn btn-sm" style={miniBtn} disabled={isLast} title="تأخير المشروع" onClick={() => onMove(project.id, 1)}>▼</button>
          {project.source === 'platform' && (
            <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none' }} onClick={() => setConfiguring((v) => !v)}>⚙ ضبط الجداول</button>
          )}
          {project.source === 'platform' && !project.sheikh_id && (
            <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none' }} title="مشروع تقرير مستقل لكل شيخ" onClick={splitBySheikh}>👥 توزيع حسب المشايخ</button>
          )}
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none' }} onClick={toggleEditing}>تعديل</button>
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none' }} onClick={destroy}>حذف</button>
        </div>
      </div>

      {editing && (
        <div style={{ padding: 12, background: 'var(--surface-alt)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          <Field label="اسم المشروع"><input className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="الشيخ / الجهة"><input className="form-input" value={form.sheikh_name} onChange={(e) => setForm({ ...form, sheikh_name: e.target.value })} /></Field>
          <Field label="ملاحظات أسفل الجداول (كل سطر ملاحظة)">
            <textarea className="form-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <Field label="ضمن مشروع موحّد">
            <select className="form-input" value={form.group_id} onChange={(e) => setForm({ ...form, group_id: e.target.value })}>
              <option value="">— مستقل —</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
            </select>
          </Field>
          {project.source === 'platform' && (
            <Field label="مشروع المنصة">
              <select className="form-input" value={form.platform_project_id} onChange={(e) => setForm({ ...form, platform_project_id: e.target.value })}>
                {platformProjects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </Field>
          )}
          {project.source === 'platform' && (
            <Field label="أعمال شيخ محدد فقط">
              <select className="form-input" value={form.sheikh_id} onChange={(e) => setForm({ ...form, sheikh_id: e.target.value })}>
                <option value="">كل المهام</option>
                {sheikhs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          )}
          {project.source === 'platform' && (
            <Field label="نطاق الأعمال">
              <select className="form-input" value={form.period_mode} onChange={(e) => setForm({ ...form, period_mode: e.target.value })}>
                <option value="cumulative">مستمر — كل أعمال المشروع</option>
                <option value="monthly">الأعمال النشطة خلال شهر التقرير</option>
              </select>
              {form.period_mode === 'monthly' && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  يشمل ما بدأ قبل الشهر واستمر خلاله، وما بدأ أو اكتمل داخله — المنجز في أشهر سابقة لا يظهر.
                </div>
              )}
            </Field>
          )}
          <Field label="لون القسم وجداوله">
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="color" value={form.color || '#143A67'} onChange={(e) => setForm({ ...form, color: e.target.value })} style={{ width: 44, height: 30, padding: 0, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }} />
              {form.color && <button className="btn btn-sm btn-outline" onClick={() => setForm({ ...form, color: '' })}>الافتراضي</button>}
            </div>
          </Field>
          <div style={{ alignSelf: 'end' }}><button className="btn btn-primary btn-sm" onClick={saveProject}>حفظ</button></div>
        </div>
      )}

      {project.source === 'platform' ? (
        <div style={{ padding: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
            الجداول تُشتق تلقائياً من مهام المشروع (جدول لكل مسار، صفوفه مهامه) وتتحدث لحظياً — لا تحتاج تعبئة يدوية.
            {project.period_mode === 'monthly' && ' هذا القسم يعرض الأعمال النشطة خلال شهر التقرير المحدد في خانة النشر أعلاه فقط.'}
          </div>
          {configuring && (
            <MatrixConfigEditor project={project} api={api} toast={toast}
              onSaved={() => { setConfiguring(false); reload(); }} onCancel={() => setConfiguring(false)} />
          )}
          {previewProject
            ? (previewProject.matrices.length
                ? previewProject.matrices.map((m, i) => <PrMatrixTable key={i} matrix={m} sectionColor={project.color} />)
                : <div className="empty">لا مهام في مشروع المنصة المرتبط بعد.</div>)
            : preview === 'error'
              ? <div style={{ fontSize: 13, color: 'var(--danger)' }}>تعذّر تحميل المعاينة — أعد تحميل الصفحة.</div>
              : <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>جاري تحميل المعاينة...</div>}
        </div>
      ) : (
        <div style={{ padding: 12 }}>
          {(project.matrices || []).map((matrix, idx) => (
            <MatrixEditor key={matrix.id} matrix={matrix} api={api} toast={toast} reload={reload} setReport={setReport} projectId={project.id}
              onMove={moveMatrix} isFirst={idx === 0} isLast={idx === (project.matrices.length - 1)} />
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input className="form-input" style={{ maxWidth: 260 }} placeholder="عنوان جدول جديد (المرحلة الأولى...)" value={newMatrix} onChange={(e) => setNewMatrix(e.target.value)} />
            <button className="btn btn-outline btn-sm" onClick={addMatrix}>＋ جدول</button>
          </div>
        </div>
      )}
      {project.notes && <div style={{ padding: '0 14px 12px', fontSize: 13, whiteSpace: 'pre-line' }}><strong>ملاحظة:</strong> {project.notes}</div>}
    </div>
  );
}

// المشاريع الموحّدة: مجموعات تجمع أقساماً (أعمال المشايخ) تحت عنوان جامع في التقرير
function GroupsManager({ reportId, groups, api, toast, reload }) {
  const [title, setTitle] = useState('');

  const create = async () => {
    if (!title.trim()) return;
    try {
      await api(`/progress/reports/${reportId}/groups`, { method: 'POST', body: JSON.stringify({ title: title.trim() }) });
      toast.success('أُنشئ المشروع الموحّد — اختره من «تعديل» في الأقسام المراد ضمّها');
      setTitle('');
      reload();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <details style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '10px 12px', marginTop: 10 }} open={groups.length > 0}>
      <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
        🗂 المشاريع الموحّدة ({groups.length})
        <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted)', marginInlineStart: 8 }}>
          تجمع أقسام المشايخ تحت عنوان جامع واحد في التقرير
        </span>
      </summary>
      <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
        {groups.map((g) => <GroupRow key={g.id} group={g} api={api} toast={toast} reload={reload} />)}
        <div style={{ display: 'flex', gap: 8 }}>
          <input className="form-input" style={{ maxWidth: 320 }} placeholder="مشروع موحّد جديد (مشروع خدمة الدروس العلمية...)"
            value={title} onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()} />
          <button className="btn btn-outline btn-sm" onClick={create} disabled={!title.trim()}>＋ إضافة</button>
        </div>
      </div>
    </details>
  );
}

function GroupRow({ group, api, toast, reload }) {
  const [form, setForm] = useState({ title: group.title, notes: group.notes || '', color: group.color || '' });
  const dirty = form.title !== group.title || form.notes !== (group.notes || '') || form.color !== (group.color || '');

  const save = async () => {
    try {
      await api(`/progress/groups/${group.id}`, {
        method: 'PUT',
        body: JSON.stringify({ title: form.title, notes: form.notes || null, color: form.color || null }),
      });
      toast.success('حُفظ المشروع الموحّد');
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const destroy = async () => {
    if (!window.confirm(`حذف المشروع الموحّد «${group.title}»؟ (تبقى الأقسام في التقرير دون تجميع)`)) return;
    try { await api(`/progress/groups/${group.id}`, { method: 'DELETE' }); reload(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, alignItems: 'end', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
      <Field label="العنوان">
        <input className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      </Field>
      <Field label="ملاحظات المشروع (تظهر أسفل أقسامه)">
        <textarea className="form-input" rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      </Field>
      <Field label="لون الترويسة">
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="color" value={form.color || '#1f5c3f'} onChange={(e) => setForm({ ...form, color: e.target.value })}
            style={{ width: 44, height: 30, padding: 0, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }} />
          {form.color && <button className="btn btn-sm btn-outline" onClick={() => setForm({ ...form, color: '' })}>الافتراضي</button>}
        </div>
      </Field>
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || !form.title.trim()}>حفظ</button>
        <button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)' }} onClick={destroy}>حذف</button>
      </div>
    </div>
  );
}

function MatrixEditor({ matrix, api, toast, reload, setReport, projectId, onMove, isFirst, isLast }) {
  const [structure, setStructure] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [newCol, setNewCol] = useState('');
  const [newRow, setNewRow] = useState('');

  const cellOf = (rowId, colId) => (matrix.cells || []).find((c) => c.row_id === rowId && c.column_id === colId);

  // تحديث تفاؤلي محلي لخلية دون إعادة تحميل كامل
  const patchCell = (rowId, colId, values) => {
    setReport((rep) => ({
      ...rep,
      projects: rep.projects.map((p) => p.id !== projectId ? p : {
        ...p,
        matrices: p.matrices.map((m) => {
          if (m.id !== matrix.id) return m;
          const exists = (m.cells || []).some((c) => c.row_id === rowId && c.column_id === colId);
          const cells = exists
            ? m.cells.map((c) => (c.row_id === rowId && c.column_id === colId) ? { ...c, ...values } : c)
            : [...(m.cells || []), { matrix_id: m.id, row_id: rowId, column_id: colId, status: null, text_value: null, ...values }];
          return { ...m, cells };
        }),
      }),
    }));
  };

  const setCell = async (rowId, colId, values) => {
    patchCell(rowId, colId, values);
    try {
      await api(`/progress/matrices/${matrix.id}/cells`, {
        method: 'PUT',
        body: JSON.stringify({ row_id: rowId, column_id: colId, status: values.status ?? null, text_value: values.text_value ?? null }),
      });
    } catch (e) { toast.error(e.message); reload(); }
  };

  const cycle = (rowId, colId) => {
    const current = cellOf(rowId, colId);
    if (textMode) return;
    if (current?.text_value && !window.confirm(`استبدال النص «${current.text_value}» بحالة؟`)) return;
    const idx = PR_CYCLE.indexOf(current?.status ?? null);
    const next = PR_CYCLE[(idx + 1) % PR_CYCLE.length];
    setCell(rowId, colId, { status: next, text_value: null });
  };

  const rename = async (kind, item) => {
    const label = window.prompt('الاسم الجديد:', item.label);
    if (!label || label === item.label) return;
    try { await api(`/progress/${kind}/${item.id}`, { method: 'PUT', body: JSON.stringify({ label }) }); reload(); } catch (e) { toast.error(e.message); }
  };

  const removeItem = async (kind, item) => {
    if (!window.confirm(`حذف «${item.label}» وكل خلاياه؟`)) return;
    try { await api(`/progress/${kind}/${item.id}`, { method: 'DELETE' }); reload(); } catch (e) { toast.error(e.message); }
  };

  const add = async (kind, label, clear) => {
    if (!label) return;
    try { await api(`/progress/matrices/${matrix.id}/${kind}`, { method: 'POST', body: JSON.stringify({ label }) }); clear(); reload(); } catch (e) { toast.error(e.message); }
  };

  const moveColumn = async (colId, dir) => {
    const ids = moveId(matrix.columns, colId, dir);
    if (!ids) return;
    try {
      await api(`/progress/matrices/${matrix.id}/reorder-columns`, { method: 'PUT', body: JSON.stringify({ column_ids: ids }) });
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const moveRow = async (rowId, dir) => {
    const ids = moveId(matrix.rows, rowId, dir);
    if (!ids) return;
    try {
      await api(`/progress/matrices/${matrix.id}/reorder-rows`, { method: 'PUT', body: JSON.stringify({ row_ids: ids }) });
      reload();
    } catch (e) { toast.error(e.message); }
  };

  const renameMatrix = async () => {
    const title = window.prompt('عنوان الجدول:', matrix.title || '');
    if (title === null) return;
    try { await api(`/progress/matrices/${matrix.id}`, { method: 'PUT', body: JSON.stringify({ title: title || null }) }); reload(); } catch (e) { toast.error(e.message); }
  };

  const setColor = async (color) => {
    try { await api(`/progress/matrices/${matrix.id}`, { method: 'PUT', body: JSON.stringify({ color }) }); reload(); } catch (e) { toast.error(e.message); }
  };

  const destroyMatrix = async () => {
    if (!window.confirm('حذف هذا الجدول بكل بنوده؟')) return;
    try { await api(`/progress/matrices/${matrix.id}`, { method: 'DELETE' }); reload(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: matrix.color || 'var(--report-brand)', color: '#fff', borderRadius: '6px 6px 0 0', padding: '6px 10px' }}>
        <strong style={{ fontSize: 14 }}>{matrix.title || 'جدول'}</strong>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="color" title="لون الجدول" value={matrix.color || '#F2CA1B'} onChange={(e) => setColor(e.target.value)}
            style={{ width: 26, height: 22, padding: 0, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }} />
          {matrix.color && <button className="btn btn-sm" style={miniBtn} title="اللون الافتراضي" onClick={() => setColor(null)}>↺</button>}
          <button className="btn btn-sm" style={miniBtn} disabled={isFirst} title="تقديم الجدول" onClick={() => onMove(matrix.id, -1)}>▲</button>
          <button className="btn btn-sm" style={miniBtn} disabled={isLast} title="تأخير الجدول" onClick={() => onMove(matrix.id, 1)}>▼</button>
          <button className="btn btn-sm" style={miniBtn} onClick={() => setTextMode((v) => !v)} title="التبديل بين حالة/نص">{textMode ? '✓ وضع النص' : 'نص'}</button>
          <button className="btn btn-sm" style={miniBtn} onClick={() => setStructure((v) => !v)}>{structure ? '✓ البنية' : 'البنية'}</button>
          <button className="btn btn-sm" style={miniBtn} onClick={renameMatrix}>✎</button>
          <button className="btn btn-sm" style={miniBtn} onClick={destroyMatrix}>🗑</button>
        </div>
      </div>
      <div className="table-wrap">
        <table className="report-matrix">
          <thead>
            <tr>
              <th className="report-matrix-rowhead">البيــان</th>
              {matrix.columns.map((c, ci) => (
                <th key={c.id}>
                  {c.label}
                  {structure && (
                    <span style={{ marginInlineStart: 6, whiteSpace: 'nowrap' }}>
                      {ci > 0 && <span style={{ cursor: 'pointer' }} title="تقديم المرحلة" onClick={() => moveColumn(c.id, -1)}>→</span>}{' '}
                      {ci < matrix.columns.length - 1 && <span style={{ cursor: 'pointer' }} title="تأخير المرحلة" onClick={() => moveColumn(c.id, 1)}>←</span>}{' '}
                      <span style={{ cursor: 'pointer' }} onClick={() => rename('columns', c)}>✎</span>{' '}
                      <span style={{ cursor: 'pointer' }} onClick={() => removeItem('columns', c)}>×</span>
                    </span>
                  )}
                </th>
              ))}
              {structure && (
                <th>
                  <input className="form-input" style={{ minWidth: 110, padding: '2px 6px', fontSize: 12 }} placeholder="＋ مرحلة" value={newCol}
                    onChange={(e) => setNewCol(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && add('columns', newCol, () => setNewCol(''))} />
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {matrix.rows.map((r, ri) => (
              <tr key={r.id}>
                <td className="report-matrix-rowhead">
                  {r.label}
                  {structure && (
                    <span style={{ marginInlineStart: 6, whiteSpace: 'nowrap' }}>
                      {ri > 0 && <span style={{ cursor: 'pointer' }} title="تقديم البند" onClick={() => moveRow(r.id, -1)}>▲</span>}{' '}
                      {ri < matrix.rows.length - 1 && <span style={{ cursor: 'pointer' }} title="تأخير البند" onClick={() => moveRow(r.id, 1)}>▼</span>}{' '}
                      <span style={{ cursor: 'pointer' }} onClick={() => rename('rows', r)}>✎</span>{' '}
                      <span style={{ cursor: 'pointer', color: 'var(--danger)' }} onClick={() => removeItem('rows', r)}>×</span>
                    </span>
                  )}
                </td>
                {matrix.columns.map((c) => {
                  const cell = cellOf(r.id, c.id);
                  return (
                    <td key={c.id}>
                      {textMode ? (
                        <input className="form-input" style={{ maxWidth: 110, padding: '2px 6px', fontSize: 12, textAlign: 'center' }}
                          key={`${r.id}:${c.id}:${cell?.text_value || ''}`}
                          defaultValue={cell?.text_value || ''}
                          onBlur={(e) => (e.target.value || '') !== (cell?.text_value || '') && setCell(r.id, c.id, { text_value: e.target.value || null, status: null })} />
                      ) : (
                        <PrStatusIcon status={cell?.status} text={cell?.text_value} onClick={() => cycle(r.id, c.id)} title="اضغط لتغيير الحالة" />
                      )}
                    </td>
                  );
                })}
                {structure && <td />}
              </tr>
            ))}
            <tr>
              <td className="report-matrix-rowhead">
                <input className="form-input" style={{ minWidth: 140, padding: '2px 6px', fontSize: 12 }} placeholder="＋ بند جديد (Enter)" value={newRow}
                  onChange={(e) => setNewRow(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && add('rows', newRow, () => setNewRow(''))} />
              </td>
              <td colSpan={matrix.columns.length + (structure ? 1 : 0)} style={{ color: 'var(--text-faint)', fontSize: 12 }}>
                {matrix.columns.length === 0 ? 'أضف المراحل من زر «البنية»' : ''}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ضبط الجداول المشتقة: إعادة تسمية، دمج مجموعات، إخفاء، ونقل مهمة مفردة بين الجداول
function MatrixConfigEditor({ project, api, toast, onSaved, onCancel }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api(`/progress/projects/${project.id}/derived`)
      .then((d) => {
        setData(d);
        const c = d.matrix_config || {};
        setCfg({
          groups: c.groups || {},
          tasks: c.tasks || {},
          hide_other: !!c.hide_other,
          other_title: c.other_title || '',
        });
      })
      .catch((e) => toast.error(e.message));
  }, [api, project.id]);

  if (!data || !cfg) return <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 8 }}>جاري تحميل إعدادات الجداول...</div>;

  const wfGroups = data.groups.filter((g) => g.key !== 'other');
  const setGroup = (key, patch) => setCfg((c) => ({ ...c, groups: { ...c.groups, [key]: { ...(c.groups[key] || {}), ...patch } } }));
  const setTaskMove = (taskId, dest) => setCfg((c) => {
    const tasks = { ...c.tasks };
    if (dest) tasks[String(taskId)] = dest; else delete tasks[String(taskId)];
    return { ...c, tasks };
  });

  const submit = async () => {
    setSaving(true);
    try {
      // تنظيف: لا نخزّن مفاتيح فارغة
      const groups = {};
      Object.entries(cfg.groups).forEach(([k, g]) => {
        const clean = {};
        if (g.title) clean.title = g.title;
        if (g.merge_into) clean.merge_into = g.merge_into;
        if (g.hidden) clean.hidden = true;
        if (g.color) clean.color = g.color;
        if (Object.keys(clean).length) groups[k] = clean;
      });
      const config = {};
      if (Object.keys(groups).length) config.groups = groups;
      if (Object.keys(cfg.tasks).length) config.tasks = cfg.tasks;
      if (cfg.hide_other) config.hide_other = true;
      if (cfg.other_title) config.other_title = cfg.other_title;

      await api(`/progress/projects/${project.id}`, {
        method: 'PUT',
        body: JSON.stringify({ matrix_config: Object.keys(config).length ? config : null }),
      });
      toast.success('حُفظ ضبط الجداول');
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const destOptions = (currentKey) => [
    ...wfGroups.filter((g) => g.key !== currentKey).map((g) => ({ value: g.key, label: cfg.groups[g.key]?.title || g.title })),
    ...(currentKey !== 'other' ? [{ value: 'other', label: cfg.other_title || 'أعمال أخرى' }] : []),
  ];

  return (
    <div style={{ background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 10 }}>⚙ ضبط الجداول المشتقة</div>

      {data.groups.length === 0 && <div className="empty">لا مهام في مشروع المنصة بعد.</div>}

      {data.groups.map((g) => {
        const isOther = g.key === 'other';
        const gc = cfg.groups[g.key] || {};
        return (
          <div key={g.key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 10, background: 'var(--surface)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, alignItems: 'end' }}>
              <Field label={isOther ? 'عنوان «أعمال أخرى»' : `عنوان الجدول (الأصل: ${g.title})`}>
                <input className="form-input" placeholder={g.title}
                  value={isOther ? cfg.other_title : (gc.title || '')}
                  onChange={(e) => isOther ? setCfg((c) => ({ ...c, other_title: e.target.value })) : setGroup(g.key, { title: e.target.value })} />
              </Field>
              {!isOther && (
                <Field label="دمج هذا الجدول في">
                  <select className="form-input" value={gc.merge_into || ''} onChange={(e) => setGroup(g.key, { merge_into: e.target.value })}>
                    <option value="">— بلا دمج —</option>
                    {destOptions(g.key).filter((o) => o.value !== 'other').map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </Field>
              )}
              {!isOther && (
                <Field label="لون الجدول">
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="color" value={gc.color || '#143A67'} onChange={(e) => setGroup(g.key, { color: e.target.value })}
                      style={{ width: 44, height: 30, padding: 0, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }} />
                    {gc.color && <button className="btn btn-sm btn-outline" onClick={() => setGroup(g.key, { color: '' })}>الافتراضي</button>}
                  </div>
                </Field>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 8 }}>
                <input type="checkbox"
                  checked={isOther ? cfg.hide_other : !!gc.hidden}
                  onChange={(e) => isOther ? setCfg((c) => ({ ...c, hide_other: e.target.checked })) : setGroup(g.key, { hidden: e.target.checked })} />
                إخفاء من التقرير
              </label>
            </div>

            {g.tasks.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)' }}>مهام هذا الجدول ({g.tasks.length}) — نقل مهمة لجدول آخر</summary>
                <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                  {g.tasks.map((t) => (
                    <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                      <span style={{ flex: 1 }}>{t.title}</span>
                      <select className="form-input" style={{ maxWidth: 220, padding: '2px 6px', fontSize: 12 }}
                        value={cfg.tasks[String(t.id)] || ''}
                        onChange={(e) => setTaskMove(t.id, e.target.value)}>
                        <option value="">هنا (تلقائي)</option>
                        {destOptions(g.key).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving}>{saving ? 'جاري الحفظ...' : 'حفظ الضبط'}</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

const miniBtn = { background: 'rgba(255,255,255,0.2)', color: '#fff', border: 'none', padding: '2px 8px', fontSize: 11 };

function Field({ label, children }) {
  return (
    <div>
      <label className="form-label" style={{ fontSize: 12 }}>{label}</label>
      {children}
    </div>
  );
}

/**
 * ربط التقرير بالعميل أو الجهة الداعمة، ومسودّة شهر بنسخ السابق، وإرسال المنشور
 * إلى البوابة أو البريد، وسجلّ كل إرسال.
 */
function ReportDeliveryPanel({ report, month, sheikhs, onChange }) {
  const { api } = useAuth();
  const toast = useToast();
  const [sponsors, setSponsors] = useState([]);
  const [log, setLog] = useState([]);
  const [channel, setChannel] = useState('email');
  const [attachPdf, setAttachPdf] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadLog = useCallback(() => {
    api(`/progress/reports/${report.id}/deliveries`).then((r) => setLog(Array.isArray(r) ? r : [])).catch(() => {});
  }, [api, report.id]);
  useEffect(() => { loadLog(); }, [loadLog]);
  useEffect(() => { api('/sponsors').then((r) => setSponsors(Array.isArray(r) ? r : [])).catch(() => {}); }, [api]);

  const link = async (patch) => {
    try { await api(`/progress/reports/${report.id}`, { method: 'PUT', body: JSON.stringify(patch) }); onChange(); }
    catch (e) { toast.error(e.message); }
  };

  const draft = async () => {
    setBusy(true);
    try {
      await api(`/progress/reports/${report.id}/periods`, { method: 'POST', body: JSON.stringify({ month }) });
      toast.success(`أُنشئت مسودّة ${month} بنسخ آخر شهر منشور`);
      onChange();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const send = async () => {
    const target = report.client_id ? { client_id: report.client_id } : { sponsor_id: report.sponsor_id };
    if (!window.confirm(`إرسال تقرير ${month} إلى الجهة المربوطة؟`)) return;
    setBusy(true);
    try {
      const r = await api('/progress/reports/send', { method: 'POST', body: JSON.stringify({ ...target, month, channel, attach_pdf: attachPdf }) });
      toast.success(r.message);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); loadLog(); }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="report-delivery">
      <div className="card-title">📨 الإرسال</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
        <select className="form-select" style={{ width: 220 }} value={report.client_id || ''}
          onChange={(e) => link({ client_id: e.target.value ? Number(e.target.value) : null })}>
          <option value="">— الشيخ/العميل المستلم —</option>
          {sheikhs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="form-select" style={{ width: 220 }} value={report.sponsor_id || ''}
          onChange={(e) => link({ sponsor_id: e.target.value ? Number(e.target.value) : null })}>
          <option value="">— الجهة الداعمة المستلمة —</option>
          {sponsors.map((sp) => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
        <button className="btn btn-outline btn-sm" disabled={busy} onClick={draft}>🗒 مسودّة {month} بنسخ السابق</button>
        <select className="form-select" style={{ width: 150 }} value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="email">البريد</option>
          <option value="portal">بوابة العميل</option>
          <option value="both">كلاهما</option>
        </select>
        <label style={{ fontSize: 12, display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} /> إرفاق PDF
        </label>
        <button className="btn btn-primary btn-sm" disabled={busy || (!report.client_id && !report.sponsor_id)} onClick={send}>إرسال تقرير {month}</button>
      </div>
      {log.length > 0 && (
        <table className="table" style={{ marginTop: 12, fontSize: 12 }}>
          <thead><tr><th>متى</th><th>الشهر</th><th>القناة</th><th>إلى</th><th>الحالة</th><th>من</th></tr></thead>
          <tbody>
            {log.map((d) => (
              <tr key={d.id}>
                <td>{d.sent_at?.slice(0, 16).replace('T', ' ')}</td>
                <td>{d.period?.report_month?.slice(0, 7)}</td>
                <td>{d.channel === 'portal' ? 'البوابة' : 'البريد'}{d.with_pdf ? ' + PDF' : ''}</td>
                <td dir="ltr">{d.address || '—'}</td>
                <td>{d.status === 'sent' ? '✓ أُرسل' : `✗ ${d.error || 'فشل'}`}</td>
                <td>{d.sender?.name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
